<template>
  <div
    ref="rootElement"
    class="options-list"
    :style="
      maxHeight
        ? {
            'max-height': maxHeight
          }
        : {}
    "
    tabindex="-1"
    @keydown.down.stop.prevent="onKeyDown($event)"
    @keydown.up.stop.prevent="onKeyUp($event)"
    @keydown.escape.stop.prevent="$emit('close')"
  >
    <!-- OPTIONS HEADER -->
    <div v-if="$slots.header" class="options-list__header">
      <slot name="header"></slot>
    </div>

    <slot name="before-options" :close="() => $emit('close')"></slot>

    <!-- OPTIONS -->
    <template v-for="(optionKey, index) in filteredOptionKeys" :key="optionKey">
      <template
        v-if="
          groupKey != null &&
          options[optionKey]?.[groupKey] !=
            options?.[filteredOptionKeys?.[index - 1]]?.[groupKey]
        "
      >
        <slot
          name="grouplabel"
          :value="options[optionKey][groupKey]"
          :option-key="optionKey"
        >
          <span :key="optionKey + 'label'" class="options-list__grouplabel">{{
            options[optionKey][groupKey]
          }}</span>
        </slot>
      </template>
      <div class="options-list__line" :class="{ '-selected': selectedKey === optionKey }">
        <button
          :ref="(el) => setOptionButtonEntry(el as HTMLButtonElement, optionKey)"
          class="options-list__option"
          :class="{
            'is-selected': selectedKey === optionKey
          }"
          :disabled="options[optionKey]?.disabled"
          type="button"
          @click.prevent="onSelect(optionKey)"
          @pointerdown="onOptionPointerDown($event)"
          @pointerover="$emit('update:hoveredOption', options[optionKey])"
          @pointerout="$emit('update:hoveredOption', null)"
          @focus="$emit('update:selectedKey', optionKey)"
        >
          <!--
            <lucide-icon
              v-if="options[optionKey]?.icon != null"
              :name="options[optionKey].icon"
              class="options-list__icon"
            />
          -->
          <slot :item-key="optionKey" :item="options[optionKey]">
            <template v-if="prop != null && (typeof options[optionKey] != 'string')">
              {{ options[optionKey][prop] }}
            </template>
            <template v-else>
              {{ options[optionKey] }}
            </template>
          </slot>
        </button>
        <slot name="after-option" :item="options[optionKey]"></slot>
      </div>
    </template>

    <slot name="after-options" :close="() => $emit('close')"></slot>

    <div
      v-if="optionsCount > 0 && filteredOptionKeys.length == 0"
      class="options-list__text"
    >
      {{ $t("messages.noResults") }}
    </div>

    <!-- EMPTY MESSAGE -->
    <slot v-if="empty" name="empty">
      <span class="options-list__text">
        {{ emptyText != null ? emptyText : $t("errors.noEntries") }}
      </span>
    </slot>

    <footer v-if="$slots.footer" class="options-list__after">
      <slot name="footer"></slot>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, nextTick, onMounted, watch } from "vue";
import { size, filter } from "lodash-es";

import { getScrollParent } from "@/helpers/utility/DOM";

const props = withDefaults(defineProps<{
  options: Record<string | number, any>;
  selectedKey?: string | number | null;
  orderedKeys?: (string | number)[];
  prop?: string | null;
  searchString?: string;
  maxOptionsHeight?: number | null;
  trapFocus?: boolean;
  groupKey?: string | null;
  emptyText?: string | null;
  limitHeight?: boolean;
}>(), {
  selectedKey: null,
  orderedKeys: () => [],
  prop: "title",
  searchString: "",
  maxOptionsHeight: null,
  trapFocus: false,
  groupKey: null,
  emptyText: null,
  limitHeight: true
});

const emit = defineEmits<{
  (event: "update:selectedKey", value: string | number | null): void;
  (event: "update:hoveredOption", value: any | null): void;
  (event: "update:fileData", value: any): void;
  (event: "select", value: { key: string | number; wasSelected: boolean }): void;
  (event: "focus-out", value: "top" | "bottom"): void;
  (event: "close"): void;
}>();

const rootElement = ref<HTMLElement | null>(null);
const optionButtons = ref<Record<string | number, HTMLButtonElement>>({});

function setOptionButtonEntry(el: HTMLButtonElement | null, optionKey: string | number) {
  if (el == null) {
    delete optionButtons.value[optionKey];
  } else {
    optionButtons.value[optionKey] = el;
  }
}

const state = reactive({
  calculatedMaxHeight: null as number | null
});

const optionsCount = computed(() => {
  return size(props.options);
});

const filteredOptionKeys = computed(() => {
  if (props.searchString == "") {
    return props.orderedKeys;
  }
  return filter(props.orderedKeys, (optionKey) => {
    const key = optionKey as keyof typeof props.options;
    const option = props.options[key];
    let textToSearch = "";
    if (option == null) return false;
    else if (typeof option === "string") textToSearch = option;
    else if (typeof option === "number") textToSearch = `${option}`;
    else if (typeof option === "object") {
      if (props.prop && (props.prop in option)) {
        textToSearch = option[props.prop as keyof typeof option];
      }
      else if ("title" in option) textToSearch += option.title;
      else if ("name" in option) textToSearch += option.name;
    }
    return textToSearch
      .toLowerCase()
      .includes(props.searchString.toLowerCase());
  });
});

/**
 * Check if the options are empty
 */
const empty = computed(() => {
  return optionsCount.value === 0;
});

const maxHeight = computed(() => {
  if (state.calculatedMaxHeight) {
    return `${state.calculatedMaxHeight}px`;
  }
  return props.maxOptionsHeight;
});

watch(() => props.searchString, (value) => {
  // Clear any selected key when changing the search string
  emit("update:selectedKey", null);
});

onMounted(async () => {
  /**
   * Adjust to the available height first,
   * so that following scroll events aren't
   * exceeding available height.
   */
  limitToWindow();

  await nextTick();
  
  // focus the selected option
  if (props.selectedKey != null) {
    const optionButton = optionButtons.value[props.selectedKey];
    if (optionButton) {
      optionButton.focus();
    }
  }
});


function focus() {
  if (props.selectedKey == null) {
    const optionKeys = Object.keys(optionButtons.value);
    if (optionKeys.length > 0) {
      optionButtons.value[optionKeys[0]].focus();
    }
  } else {
    const optionButton = optionButtons.value[props.selectedKey];
    if (optionButton) {
      optionButton.focus();
    } else {
      emit("update:selectedKey", null);
    }
  }
}

function onKeyUp(event: KeyboardEvent) {
  event.preventDefault();
  const optionKeys = filteredOptionKeys.value;

  let nextIndex = 0;
  if (props.selectedKey == null) {
    nextIndex = optionKeys.length - 1;
  } else {
    nextIndex = optionKeys.indexOf(`${props.selectedKey}`) - 1;
  }

  // jump over any disabled options:
  while (nextIndex >= 0 && props.options[optionKeys[nextIndex]]?.disabled) {
    nextIndex--;
  }

  if (nextIndex < 0) {
    // reached top of the list
    if (!props.trapFocus) {
      emit("update:selectedKey", null);
      emit("focus-out", "top");
      return;
    } else {
      nextIndex = optionKeys.length - 1;
    }
  }

  selectIndex(optionKeys[nextIndex]);
}
function onKeyDown(event: KeyboardEvent) {
  event.preventDefault();
  const optionKeys = filteredOptionKeys.value;

  let nextIndex = 0;
  if (props.selectedKey != null) {
    nextIndex = optionKeys.indexOf(`${props.selectedKey}`) + 1;
  }

  // jump over any disabled options
  while (props.options[optionKeys[nextIndex]]?.disabled) {
    nextIndex++;
  }

  if (nextIndex >= optionKeys.length) {
    // reached bottom of the list
    if (!props.trapFocus) {
      emit("update:selectedKey", null);
      emit("focus-out", "bottom");
      return;
    } else {
      nextIndex = 0;
    }
  }

  selectIndex(optionKeys[nextIndex]);
}

function onSelect(optionKey: string | number, fileData = null) {
  const wasSelected = props.selectedKey === optionKey;

  if (!wasSelected) {
    selectIndex(optionKey);
    if (fileData !== null) {
      emit("update:fileData", fileData);
    }
  }

  emit("select", { key: optionKey, wasSelected });
}

function selectIndex(optionKey: string | number) {
  const button = optionButtons.value[optionKey];
  if (!button) {
    console.warn("Button not found for index", optionKey, optionButtons.value);
    return;
  }
  button.focus();
}

/**
 * Prevent default pointer down event
 * To ensure that the option is only selected
 * on click.
 */
function onOptionPointerDown(event: PointerEvent) {
  event.preventDefault();
}

/**
 * Make sure that the dimensions of the element
 * are not exceeding the dimensions of the current viewport.
 */
async function limitToWindow() {
  if (!props.limitHeight) return;

  const element = rootElement.value;
  if (!element) return;

  const rect = element.getBoundingClientRect();

  const parentNode = getScrollParent(element.parentNode);
  const bounds = {
    top: 0,
    bottom: window.innerHeight
  };
  if (parentNode) {
    const parentRect = parentNode.getBoundingClientRect();
    bounds.top = parentRect.top;
    bounds.bottom = parentRect.bottom;
  }

  if (rect.bottom > bounds.bottom) {
    let calculatedMaxHeight = rect.height - (rect.bottom - bounds.bottom);
    if (calculatedMaxHeight < 100) {
      calculatedMaxHeight = 100;
    }
    state.calculatedMaxHeight = calculatedMaxHeight;
  } else if (rect.top < bounds.top) {
    let calculatedMaxHeight = rect.height - (bounds.top - rect.top) - 5;
    if (calculatedMaxHeight < 100) {
      calculatedMaxHeight = 100;
    }
    state.calculatedMaxHeight = calculatedMaxHeight;
  } else {
    state.calculatedMaxHeight = null;
  }
}

function getBoundingClientRect() {
  return rootElement.value?.getBoundingClientRect();
}

defineExpose({
  focus,
  getBoundingClientRect,
  onKeyUp,
  onKeyDown
});
</script>

<style lang="scss">
.options-list {
  $padding-sides: 1rem;

  background-color: var(--color-background-close);
  box-shadow: 0 2px 1em var(--color-shadow-2);
  border-radius: 5px;
  padding: 1em 0;
  min-width: 3.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100vh;

  &__header {
    padding: 0.5em 1em;
    padding-bottom: 1em;
    font-style: italic;
  }

  &__option,
  &__text {
    position: relative;
    display: block;
    width: 100%;
    padding: 0.5em 1em;
    background-color: var(--color-background-close);
    color: var(--color-text);
    font-weight: 400;
    border: none;
    outline: none;
    appearance: none;
    font-family: var(--font-family-ui);
    font-size: 1rem;
    text-align: left;
    hyphens: auto;
  }

  &__option {
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2em;

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible,
    &:not(:disabled):focus-within {
      background-color: var(--color-action);
      color: var(--color-action-contrast);
    }
    &.is-selected {
      background-color: var(--color-action);
      color: var(--color-action-contrast);
    }
    &:disabled {
      color: var(--color-text-lighter);
    }
  }

  &__line {
    display: flex;

    &.-selected,
    &:focus-within {
      color: var(--color-action-contrast);
      background-color: var(--color-action);

      & > .button-icon {
        color: inherit;
      }
    }
  }

  &__grouplabel {
    display: block;
    padding-inline-start: 1em;
    padding-inline-end: 1em;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-weight: 600;
  }

  &__icon {
    margin-right: 0.5em;
  }

  &__after {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding: 0.5em 1em;
  }

  &__subtitle {
    padding-left: var(--padding-sides, $padding-sides);
    padding-right: var(--padding-sides, $padding-sides);
  }
}
</style>
